import { getActiveFilter } from '../../../features/data-types'
import { O } from '../../../features/ui'
import { useStateHooks } from '../../../state'
import { TableName } from '../../../tables'
import { firstItem } from '../../../utils/listUtils'
import { useLoaders } from '../../common'
import { useSystemStateHooks } from '../../system'
import { DeliveryRoadmap, Roadmap } from '../state/types'

export const useRoadmapLoaders = () => {
    const systemHooks = useSystemStateHooks()
    const { findFiltered } = useStateHooks()
    
    const loaders = useLoaders()

    return (companyId = systemHooks.companyId) => {
        const findDeliveryRoadmapLink = (deliveryId: string) => {
            const roadmaps = findFiltered(TableName.DELIVERY_ROADMAPS, { deliveryId }) as DeliveryRoadmap[]
            return firstItem(roadmaps)
        }

        const loadAllRoadmaps = async (active = false) => {
            const { loadMultiple } = loaders(companyId)
            const options = active ? getActiveFilter() : undefined
            const response = await loadMultiple(TableName.ROADMAPS, options)
            return response.dataTypes as Roadmap[]
        }

        const loadRoadmap = (id: string) => {
            const { load } = loaders(companyId)
            return load(TableName.ROADMAPS, id) as Promise<O<Roadmap>>
        }

        const loadRoadmaps = (ids: string[]) => {
            const { loadByIds } = loaders(companyId)
            return loadByIds(TableName.ROADMAPS, ids) as Promise<Roadmap[]>
        }

        const loadBranchRoadmaps = async (branchId: string) => {
            const { loadFiltered, hasLoaded, addToLoaded } = loaders(companyId)
            const key = `${TableName.ROADMAPS}-${branchId}`
            const roadmaps = await loadFiltered(TableName.ROADMAPS, !hasLoaded(key), { branchId }) as Roadmap[]
            addToLoaded(key)
            return roadmaps
        }
        
        const loadVehicleRoadmaps = async (
            vehicleId: string,
            dbRequest = false
        ) => {
            const { loadFiltered } = loaders(companyId)
            return loadFiltered(TableName.ROADMAPS, dbRequest, { vehicleId }) as Promise<Roadmap[]>
        }

        const loadDeliveryRoadmapLink = async (deliveryId: string, dbRequest = false) => {
            const { loadFiltered } = loaders(companyId)
            const roadmaps = await loadFiltered(TableName.DELIVERY_ROADMAPS, dbRequest, { deliveryId }) as DeliveryRoadmap[]
            return firstItem(roadmaps)
        }

        const loadDeliveriesRoadmapsLinks = (deliveryIds: string[], dbRequest = false) => {
            const { loadFilteredByList } = loaders(companyId)
            return loadFilteredByList(TableName.DELIVERY_ROADMAPS, dbRequest, 'deliveryId', deliveryIds) as Promise<DeliveryRoadmap[]>
        }

        const loadRoadmapDeliveriesLinks = (roadmapId: string, dbRequest = false) => {
            const { loadFiltered } = loaders(companyId)
            return loadFiltered(TableName.DELIVERY_ROADMAPS, dbRequest, { roadmapId }) as Promise<DeliveryRoadmap[]>
        }

        const loadRoadmapsDeliveriesLinks = (roadmapIds: string[], dbRequest = false) => {
            const { loadFilteredByList } = loaders(companyId)
            return loadFilteredByList(TableName.DELIVERY_ROADMAPS, dbRequest, 'roadmapId', roadmapIds) as Promise<DeliveryRoadmap[]>
        }

        const countRoadmaps = (propName: string, propValue: string) => {
            const options = {
                valueFilters: [{
                    value: propValue,
                    prop: propName
                }],
                limit: 1
            }
            const { count2 } = loaders(companyId)
            return count2(TableName.ROADMAPS, options)
        }
        
        return {
            findDeliveryRoadmapLink,
            loadAllRoadmaps,
            loadRoadmap,
            loadRoadmaps,
            loadBranchRoadmaps,
            loadVehicleRoadmaps,
            loadDeliveryRoadmapLink,
            loadDeliveriesRoadmapsLinks,
            loadRoadmapDeliveriesLinks,
            loadRoadmapsDeliveriesLinks,
            countRoadmaps
        }
    }
}
